/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Pinned sticky track that maps document scroll to AboutScene progress.
 * Reduced-motion and no-WebGL visitors get a 2D chapter strip instead.
 */

import { lazy, useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import { ThreeSceneShell } from '@/components/ThreeSceneShell';
import { ABOUT_JOURNEY, type JourneyChapter } from '@/lib/aboutJourney';
import { useAboutJourneyProgress } from '@/hooks/useAboutJourneyProgress';
import { analytics } from '@/lib/analytics';
import { bindJourneyDemoApi, useJourneyDemo } from '@/lib/journeyDemo';
import { readSiteHeaderHeight } from '@/lib/siteHeader';

const AboutSceneLazy = lazy(() =>
  import('@/components/AboutScene').then((m) => ({ default: m.AboutScene })),
);

function jumpToResume(chapter: JourneyChapter) {
  const id = chapter.experienceIds[0];
  const el = (id ? document.getElementById(id) : null) ?? document.getElementById('experience');
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function AboutJourney() {
  const trackRef = useRef<HTMLDivElement>(null);
  const { progress, activeIndex } = useAboutJourneyProgress(trackRef);
  const chapter = ABOUT_JOURNEY[activeIndex];
  const demo = useJourneyDemo();
  const [reducedMotion, setReducedMotion] = useState(false);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!demo) return;
    analytics.journeyDemoOpened('query');
    return bindJourneyDemoApi(trackRef.current);
  }, [demo]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!chapter || seenRef.current.has(chapter.id)) return;
    seenRef.current.add(chapter.id);
    analytics.journeyChapterViewed(chapter.id, chapter.label);
  }, [chapter]);

  const scrollToChapter = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const last = ABOUT_JOURNEY.length - 1;
    const p = last <= 0 ? 0 : index / last;
    const header = readSiteHeaderHeight();
    const max = track.offsetHeight - (window.innerHeight - header);
    const top = window.scrollY + track.getBoundingClientRect().top - header + p * max;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  if (reducedMotion && !demo) {
    return <JourneyStrip onSelect={(index) => jumpToResume(ABOUT_JOURNEY[index])} activeIndex={0} />;
  }

  return (
    <Box
      ref={trackRef}
      id="about-journey"
      sx={{
        position: 'relative',
        minHeight: { xs: `${ABOUT_JOURNEY.length * 70}vh`, md: `${ABOUT_JOURNEY.length * 90}vh` },
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: demo ? 0 : 'var(--site-header-height)',
          height: demo ? '100dvh' : 'calc(100dvh - var(--site-header-height))',
          overflow: 'hidden',
          bgcolor: '#0f172a',
        }}
      >
        <ThreeSceneShell
          sx={{ position: 'absolute', inset: 0 }}
          message="Loading career journey…"
          fallback={
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <JourneyStrip onSelect={scrollToChapter} activeIndex={activeIndex} />
            </Box>
          }
        >
          <AboutSceneLazy progress={progress} activeIndex={activeIndex} demo={demo} />
        </ThreeSceneShell>

        {!demo && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            pt: 2,
            pb: 3,
            px: { xs: 2, md: 4 },
            pointerEvents: 'none',
            background: 'linear-gradient(to top, rgba(15,23,42,0.72) 0%, transparent 38%, transparent 62%, rgba(15,23,42,0.35) 100%)',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: '#fbbf24', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600 }}
          >
            Scroll to travel
          </Typography>

          <Stack spacing={1} sx={{ maxWidth: 560 }}>
            {!chapter.tease && (
              <>
                <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: '0.16em' }}>
                  {chapter.location}
                  {chapter.years ? ` · ${chapter.years}` : ''}
                </Typography>
                <Typography variant="h3" sx={{ color: '#f8fafc', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.75rem' } }}>
                  {chapter.label}
                </Typography>
                <Typography variant="body1" sx={{ color: '#cbd5e1' }}>
                  {chapter.subtitle}
                </Typography>
                {chapter.experienceIds.length > 0 && (
                  <Box sx={{ pointerEvents: 'auto' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        analytics.journeyResumeJumped(chapter.id, chapter.label);
                        jumpToResume(chapter);
                      }}
                      sx={{ color: '#fbbf24', px: 0, minWidth: 0, textTransform: 'none', fontWeight: 600 }}
                    >
                      Jump to resume
                    </Button>
                  </Box>
                )}
              </>
            )}
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ pointerEvents: 'auto', pt: 1 }}>
              {ABOUT_JOURNEY.map((item, index) =>
                item.tease ? null : (
                <Chip
                  key={item.id}
                  label={item.label}
                  size="small"
                  onClick={() => {
                    analytics.journeyChapterSelected(item.id, item.label, 'chip');
                    scrollToChapter(index);
                  }}
                  sx={{
                    bgcolor: index === activeIndex ? '#d97706' : 'rgba(248,250,252,0.12)',
                    color: index === activeIndex ? '#0f172a' : '#e2e8f0',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: index === activeIndex ? '#f59e0b' : 'rgba(248,250,252,0.22)' },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
        )}
      </Box>
    </Box>
  );
}

function JourneyStrip({
  onSelect,
  activeIndex,
}: {
  onSelect: (index: number) => void;
  activeIndex: number;
}) {
  return (
    <Box sx={{ bgcolor: '#0f172a', color: 'white', py: { xs: 4, md: 6 }, px: { xs: 2, md: 4 }, width: '100%' }}>
      <Typography variant="caption" sx={{ color: '#fbbf24', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
        Career journey
      </Typography>
      <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', mt: 3, pb: 1 }}>
        {ABOUT_JOURNEY.map((item, index) =>
          item.tease ? null : (
          <Box
            key={item.id}
            onClick={() => {
              analytics.journeyChapterSelected(item.id, item.label, 'strip');
              onSelect(index);
            }}
            sx={{
              minWidth: 180,
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: index === activeIndex ? '#d97706' : 'rgba(248,250,252,0.16)',
              bgcolor: 'rgba(30,41,59,0.6)',
            }}
          >
            {item.logo && (
              <Box
                component="img"
                src={item.logo}
                alt=""
                sx={{ height: 28, mb: 1, objectFit: 'contain', display: 'block' }}
              />
            )}
            <Typography variant="subtitle2" fontWeight={700}>{item.label}</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {item.location} {item.years}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
